import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserDto } from 'app/shared/apis/user/model/userDto';
import { TenantCompactDto } from 'app/shared/apis/user/model/tenantCompactDto';
import { AppShellService, PersonaRole } from 'app/core/app-shell/app-shell.service';
import { AuthService } from 'app/core/auth/auth.service';
import { personaFor } from 'app/core/auth/persona-mapping';
import { WorkspaceService } from 'app/shared/services/workspace/workspace.service';
import { planOffersCro } from 'app/shared/services/workspace/persona-plans';
import { SUSPENDED_HOLDING_URL } from 'app/core/auth/auth.interceptor';

/**
 * PostLoginRedirectService — the single place that decides where a user lands
 * after a successful sign-in (Google SSO or email/password).
 *
 * It resolves the persona through the SESSION-001c resolver
 * (`AppShellService.resolveAndStampPersona()` — never bypassed) and navigates
 * to that persona's home via `homeRouteForPersona()`:
 *
 *   is    → /queue
 *   fs    → /deals
 *   cro   → /portfolio  (or /home when the client's plan excludes CRO, SPEC-web-planner-026 AC-2)
 *   admin → /admin
 *
 * Keeping this logic in one service means persona-aware navigation is not
 * scattered across components. SESSION-meet-ux-002 (GAP-MEETUX-002).
 *
 * SPEC-web-planner-013 AC-12: before routing it reads the selected client's
 * status (GET /Workspace/SeatLimit). A PendingSetup client sends its Admin to
 * /setup and anyone else to /workspace-status?state=pending; a Suspended
 * client sends everyone to /workspace-status?state=suspended (AC-26); any
 * other status, or no answer, routes to the persona home as before.
 */
@Injectable({ providedIn: 'root' })
export class PostLoginRedirectService {
    constructor(
        private _router: Router,
        private _appShellService: AppShellService,
        private _authService: AuthService,
        private _workspaceService: WorkspaceService,
    ) {}

    /**
     * Resolve the persona for `user` (optionally scoped to a freshly-selected
     * tenant), stamp it via the resolver, and navigate to the persona home.
     * Returns the resolved persona so callers can log/assert on it.
     */
    redirectAfterLogin(user: UserDto | undefined, selectedTenant?: TenantCompactDto): PersonaRole {
        // Without an explicit tenant, the persona comes from the user's entry for
        // the selected client, so it matches the client whose status is read.
        const currentId = this._authService.selectedTenant?.id;
        const tenant = selectedTenant ?? (currentId ? user?.tenants?.find((t) => t.id === currentId) : undefined);
        const persona = this._appShellService.resolveAndStampPersona(user, tenant);
        const home = this._appShellService.homeRouteForPersona(persona);
        const workspaceId = selectedTenant?.id ?? currentId;
        if (!workspaceId) {
            this._router.navigateByUrl(home);
            return persona;
        }
        // AC-26s: getClientStatus also reads a 403 client_suspended as Suspended.
        this._workspaceService.getClientStatus(workspaceId).subscribe((status) => {
            // Signed out or switched client while waiting: this answer is stale.
            if (this._authService.selectedTenant?.id !== currentId) return;
            const route = this.routeFor(status, user, workspaceId, home);
            // SPEC-web-planner-026 AC-2: the status routing (setup, suspended) stays ahead of the plan
            // check. A CRO whose plan excludes CRO would land on /portfolio, which PersonaGuard refuses:
            // send it to /home. getSeatLimit shares AC-1's cached read, so when the status read succeeded this costs no
            // extra request (a legacy client with no clientStatus still has a plan to check). When both reads fail the
            // CRO goes to /portfolio and the guard bounces it.
            if (persona !== 'cro' || route !== home) {
                this._router.navigateByUrl(route);
                return;
            }
            this._workspaceService.getSeatLimit(workspaceId).subscribe((limit) => {
                if (this._authService.selectedTenant?.id !== currentId) return;
                this._router.navigateByUrl(limit && !planOffersCro(limit.plan) ? '/home' : route);
            });
        });
        return persona;
    }

    /** Where a person lands for a client in `clientStatus` (AC-12). */
    routeFor(clientStatus: string | null | undefined, user: UserDto | undefined, workspaceId: string, home: string): string {
        if (clientStatus === 'PendingSetup') {
            return personaFor(user as any, workspaceId) === 'admin' ? '/setup' : '/workspace-status?state=pending';
        }
        // SPEC-web-planner-013 AC-26: a suspended client is closed to everyone.
        if (clientStatus === 'Suspended') {
            return SUSPENDED_HOLDING_URL;
        }
        return home;
    }
}
